<template>
	<div class="voice-record-control">
		<!-- 编辑模式已有语音提示 -->
		<div v-if="initialDuration > 0 && savedDuration === 0" class="voice-existing-tip">
			已有语音：{{ initialDuration }} 秒，可重新录制或上传替换
		</div>
		<!-- 已保存语音提示 -->
		<div v-if="savedDuration > 0" class="voice-saved-tip">
			已保存语音：{{ savedDuration }} 秒，可重新录制或上传替换
		</div>

		<!-- 录音区（主入口） -->
		<div class="record-area">
			<div class="record-tip">{{ recordStateTip }}</div>
			<div class="record-duration">时长: {{ recordState === 'STOP' ? 0 : recordDuration }}s / 60s</div>
			<audio v-show="playMode === 'PLAY'" :src="playUrl" controls ref="audio" @ended="onStopAudio()"></audio>
			<div class="record-btn-group">
				<el-button round size="small" type="primary" v-show="recordState === 'STOP'" :disabled="sending" @click="onStartRecord()">开始录音</el-button>
				<el-button round size="small" type="warning" v-show="recordState === 'RUNNING'" :disabled="sending" @click="onPauseRecord()">暂停录音</el-button>
				<el-button round size="small" type="primary" v-show="recordState === 'PAUSE'" :disabled="sending" @click="onResumeRecord()">继续录音</el-button>
				<el-button round size="small" type="danger" v-show="recordState === 'RUNNING' || recordState === 'PAUSE'" :disabled="sending" @click="onCompleteRecord()">结束录音</el-button>
				<template v-if="recordState === 'COMPLETE'">
					<el-button round size="small" type="success" v-show="playMode !== 'PLAY'" :disabled="sending" @click="onPlayAudio()">试听</el-button>
					<el-button round size="small" type="warning" v-show="playMode === 'PLAY'" :disabled="sending" @click="onStopAudio()">停止</el-button>
					<el-button round size="small" type="primary" :disabled="sending" @click="onRestartRecord()">重新录音</el-button>
					<el-button round size="small" type="primary" :loading="sending" :disabled="sending" @click="onUploadRecord()">保存语音</el-button>
				</template>
			</div>
		</div>

		<!-- 文件选择区（补充入口） -->
		<div class="file-picker-area">
			<div class="picker-divider"><span>或选择音频文件上传</span></div>
			<el-upload
				class="voice-uploader"
				action="action"
				drag
				:auto-upload="false"
				:show-file-list="false"
				:disabled="sending"
				:accept="acceptTypes"
				:on-change="onFileChange"
			>
				<i class="el-icon-upload2"></i>
				<div class="el-upload__text">
					将语音文件拖到此处，或<em>点击选择文件</em>
				</div>
				<div class="el-upload__tip">
					支持 .silk / .wav / .mp3 格式（其他音频格式自动转码），时长 1~60 秒，大小不超过 5MB
				</div>
			</el-upload>
		</div>

		<div v-if="sending" class="voice-processing-tip">
			<i class="el-icon-loading"></i>{{ sendingTip }}
		</div>
	</div>
</template>

<script>
import Recorder from 'js-audio-recorder';
import { uploadVoice } from '../../api/upload.js';
import { encode, getDuration, isSilk } from '../../utils/silk/index.js';

// 微信语音标准采样率 24000Hz 单声道 16bit
const SILK_SAMPLE_RATE = 24000;
// 与后端 voice/upload 的 maxVoiceSize 保持一致
const MAX_VOICE_SIZE = 5 * 1024 * 1024;

export default {
	name: 'voiceRecordControl',
	props: {
		// 当前控件是否处于可用状态（父级对话框打开且消息类型为语音）
		active: {
			type: Boolean,
			default: true
		},
		// 编辑模式已有语音时长（秒），仅用于展示提示
		initialDuration: {
			type: Number,
			default: 0
		}
	},
	data() {
		return {
			acceptTypes: '.silk,.wav,.mp3',
			// 录音状态机 STOP、RUNNING、PAUSE、COMPLETE
			recordState: 'STOP',
			recordStateTip: '未开始',
			recordDuration: 0,
			starting: false, // 录音启动中，防止快速双击重复 start
			playMode: 'RECORD', // RECORD、PLAY
			playUrl: '', // 试听 blob URL，清理时回收
			// 转码/上传中，防止重复操作与误关闭
			sending: false,
			sendingTip: '',
			// 任一入口上传成功后的时长（秒），用于"已保存"提示
			savedDuration: 0,
			// 上传代际令牌：cleanup/销毁时自增，上传回调比对以失效过期结果
			// （仅靠 active 重检在"上传中取消对话框→重新打开"的窗口期会失效）
			uploadSeq: 0,
			// 启动代际令牌：录音启动（含重新录音）时自增，回调比对以丢弃过期结果
			// （start 为异步，关闭/重开窗口期内的迟到 resolve/reject 不得污染新状态）
			startSeq: 0,
			// 60 秒上限兜底计时器（onprocess 依赖 AudioContext 回调，后台标签页不触发）
			limitTimer: null
		}
	},
	created() {
		// 非响应式挂载录音实例：避免 Vue 递归 observe context/stream 等原生对象，
		// 且实例需跨 cleanup 复用（见 cleanup 注释），不宜随 data 重建
		this.rc = this.createRecorder();
	},
	watch: {
		// 对话框关闭或切换消息类型时，清理录音状态（防麦克风残留占用）
		active(val) {
			if (!val) {
				this.cleanup();
			}
		}
	},
	beforeDestroy() {
		// 代际递增：组件被 v-if 卸载（prop 不再更新）时同样失效在途上传/启动回调
		this.uploadSeq++;
		this.startSeq++;
		this.clearLimitTimer();
		this.rc.stop();
		this.rc.destroy();
		if (this.playUrl) {
			URL.revokeObjectURL(this.playUrl);
		}
	},
	methods: {
		// 创建录音实例：微信语音标准采样率 24000Hz 单声道 16bit
		// （js-audio-recorder 参数名为 sampleBits/numChannels），并绑定 60 秒上限自动结束
		createRecorder() {
			let rc = new Recorder({ sampleRate: SILK_SAMPLE_RATE, sampleBits: 16, numChannels: 1 });
			rc.onprocess = (duration) => {
				// 录音过程中实时刷新时长（响应式，模板展示）
				this.recordDuration = Math.floor(duration);
				// 微信语音上限 60 秒，自动结束录音
				if (duration >= 60 && this.recordState !== 'COMPLETE') {
					this.onCompleteRecord();
					this.$message.info('已达到 60 秒上限，自动结束录音');
				}
			};
			return rc;
		},
		// 控件失活（关闭对话框/切换类型）时复位全部状态并递增代际令牌，
		// 使在途上传回调失效（否则重开对话框后 active 重检会漏过过期结果）
		cleanup() {
			this.uploadSeq++;
			this.startSeq++;
			this.clearLimitTimer();
			// 无在途授权请求（starting=false）时复用实例复位：先 stop 重置库的
			// isrecording 标志（destroy 不重置，否则下次 start 会直接 reject），
			// 再 destroy 清数据停流。避免每次重建实例泄漏一个 AudioContext
			// （js-audio-recorder 构造函数为探测采样率创建的 context 从不关闭）。
			// 授权请求在途时重建实例：库无法拦截迟到的授权 resolve，重建可保证其
			// 只会落在已关闭 context 的旧实例上报错，不会污染新启动的录音。
			if (this.starting) {
				this.rc.destroy();
				this.rc = this.createRecorder();
			} else {
				this.rc.stop();
				this.rc.destroy();
			}
			this.starting = false;
			// 停止试听并回收 blob URL（模板 audio 元素与数据源一致）
			if (this.$refs.audio) {
				this.$refs.audio.pause();
			}
			if (this.playUrl) {
				URL.revokeObjectURL(this.playUrl);
				this.playUrl = "";
			}
			this.playMode = 'RECORD';
			this.recordState = 'STOP';
			this.recordStateTip = '未开始';
			this.recordDuration = 0;
			this.savedDuration = 0;
			this.sending = false;
			this.sendingTip = '';
		},
		onStartRecord() {
			// 防抖：start() 为异步操作，避免快速双击重复启动
			if (this.starting) {
				return;
			}
			this.starting = true;
			this.recordDuration = 0;
			let rc = this.rc;
			const seq = ++this.startSeq;
			rc.start().then(() => {
				// 启动期间控件被清理：主动停止迟到授权流。库的 start() 不 resolve
				// stream，授权已 resolve 时流挂在实例的 stream 字段上（授权在途时
				// 无法取得引用，属库缺陷，流会残留占用直至页面刷新）
				if (seq !== this.startSeq) {
					if (rc.stream && rc.stream.getTracks) {
						rc.stream.getTracks().forEach(t => t.stop());
					}
					return;
				}
				this.recordState = 'RUNNING';
				this.recordStateTip = "正在录音...";
				this.startLimitTimer();
			}).catch(error => {
				// 过期回调（实例已被清理）：直接丢弃，避免重置新录音的状态
				if (seq !== this.startSeq) {
					return;
				}
				this.recordState = 'STOP';
				this.recordStateTip = '未开始';
				// 库在 start 失败时不重置 isrecording，stop+destroy 复位实例以恢复可用
				this.rc.stop();
				this.rc.destroy();
				this.$message.error(this.getStartErrorMsg(error));
			}).finally(() => {
				// 仅当本次启动仍是当前代际时复位防抖标志：过期 finally（如授权在途时
				// 清理后重建实例）不得提前解除新启动的防抖，否则快速双击会重复 start
				if (seq === this.startSeq) {
					this.starting = false;
				}
			});
		},
		onPauseRecord() {
			this.rc.pause();
			this.recordState = 'PAUSE';
			this.recordStateTip = "已暂停录音";
			this.clearLimitTimer();
		},
		onResumeRecord() {
			this.rc.resume();
			this.recordState = 'RUNNING';
			this.recordStateTip = "正在录音...";
			this.startLimitTimer();
		},
		onCompleteRecord() {
			this.rc.pause();
			this.recordState = 'COMPLETE';
			this.recordStateTip = "已结束录音";
			this.recordDuration = Math.floor(this.rc.duration);
			this.clearLimitTimer();
		},
		onPlayAudio() {
			let wav = this.rc.getWAVBlob();
			// 通过响应式 url 赋值驱动模板 :src 绑定，避免直接改 DOM 被重渲染覆盖
			if (this.playUrl) {
				URL.revokeObjectURL(this.playUrl);
			}
			this.playUrl = URL.createObjectURL(wav);
			this.$nextTick(() => {
				this.$refs.audio.play();
			});
			this.playMode = 'PLAY';
		},
		onStopAudio() {
			this.$refs.audio.pause();
			this.playMode = 'RECORD';
			// 停止播放即回收 blob URL，避免最长 60s 的 wav 数据（约 2.8MB）常驻内存
			if (this.playUrl) {
				URL.revokeObjectURL(this.playUrl);
				this.playUrl = "";
			}
		},
		onRestartRecord() {
			// 防抖：与 onStartRecord 一致，避免双击导致旧实例回调报错覆盖新录音状态
			if (this.starting) {
				return;
			}
			// 复用实例复位（同 cleanup）：先 stop 重置 isrecording 再 destroy 清数据，
			// 避免重建实例泄漏 AudioContext（旧录音随重新录音丢弃，符合预期）
			this.rc.stop();
			this.rc.destroy();
			this.starting = true;
			this.recordDuration = 0;
			let rc = this.rc;
			const seq = ++this.startSeq;
			rc.start().then(() => {
				// 启动期间控件被清理：主动停止迟到授权流（同 onStartRecord）
				if (seq !== this.startSeq) {
					if (rc.stream && rc.stream.getTracks) {
						rc.stream.getTracks().forEach(t => t.stop());
					}
					return;
				}
				this.recordState = 'RUNNING';
				this.playMode = 'RECORD';
				this.recordStateTip = "正在录音...";
				this.startLimitTimer();
			}).catch(error => {
				// 过期回调（实例已被清理）：直接丢弃，避免重置新录音的状态
				if (seq !== this.startSeq) {
					return;
				}
				this.recordState = 'STOP';
				this.playMode = 'RECORD';
				this.recordStateTip = '未开始';
				// 库在 start 失败时不重置 isrecording，stop+destroy 复位实例以恢复可用
				this.rc.stop();
				this.rc.destroy();
				this.$message.error(this.getStartErrorMsg(error));
			}).finally(() => {
				// 仅当本次启动仍是当前代际时复位防抖标志（同 onStartRecord）
				if (seq === this.startSeq) {
					this.starting = false;
				}
			});
		},
		// 60 秒上限兜底计时器：onprocess 依赖 AudioContext 回调，后台标签页/休眠时
		// 不触发，需基于墙上时间保证自动结束（暂停时清除，恢复时按剩余时长重启）
		startLimitTimer() {
			this.clearLimitTimer();
			const remain = Math.max(0, 60 - this.rc.duration);
			this.limitTimer = setTimeout(() => {
				this.limitTimer = null;
				if (this.recordState === 'RUNNING') {
					this.onCompleteRecord();
					this.$message.info('已达到 60 秒上限，自动结束录音');
				}
			}, remain * 1000);
		},
		clearLimitTimer() {
			if (this.limitTimer) {
				clearTimeout(this.limitTimer);
				this.limitTimer = null;
			}
		},
		// 录音启动失败的错误文案兜底（库在权限拒绝等场景 reject 时不携带错误对象）
		getStartErrorMsg(error) {
			if (error && error.message) {
				return error.message;
			}
			if (error && error.name) {
				return error.name;
			}
			return '录音启动失败';
		},
		// 录音完成：转码 silk 并上传，成功后交由父组件保存
		async onUploadRecord() {
			// 微信语音上限 60 秒（留 0.5s 容差：60 秒自动结束时 duration 可能略超 60）
			if (this.rc.duration > 60.5) {
				this.$message.warning('语音时长不能超过 60 秒，请重新录音');
				return;
			}
			// 语音过短无法编码发送
			if (this.rc.duration < 1) {
				this.$message.warning('录音太短，请重新录音');
				return;
			}
			this.sending = true;
			this.sendingTip = "正在转码上传...";
			// 捕获代际令牌：期间发生清理/销毁则丢弃结果
			const seq = this.uploadSeq;
			try {
				// wav 转 silk（传入 sampleRate=0 自动识别 wav 采样率）
				let wav = this.rc.getWAVBlob();
				let res = await encode(await wav.arrayBuffer(), 0);
				// 以实际编码时长为准校验：四舍五入后超过 60 秒的录音直接拒绝，
				// 避免 60~60.5s 容差导致真实时长超微信上限（此处拒绝不会产生上传）
				if (Math.round(res.duration / 1000) > 60) {
					this.$message.warning('语音时长不能超过 60 秒，请重新录音');
					return;
				}
				let silkBlob = new Blob([res.data], { type: 'application/octet-stream' });
				let name = new Date().getTime() + '.silk';
				let formData = new window.FormData();
				formData.append('file', silkBlob, name);
				// 上传到后端（返回 { path, size, name }）
				let data = await uploadVoice(formData);
				// 期间控件被清理（关闭/切类型/重开）则丢弃结果，避免过期语音写入表单
				if (seq !== this.uploadSeq) {
					return;
				}
				this.savedDuration = Math.min(60, Math.max(1, Math.round(res.duration / 1000)));
				this.$emit("send", {
					// 取整后钳制到 60，避免四舍五入越过后端上限校验
					duration: this.savedDuration,
					url: data.path,
					size: silkBlob.size
				});
			} catch (error) {
				this.$message.error('语音转码或上传失败：' + (error && error.message ? error.message : error));
			} finally {
				this.sending = false;
				this.sendingTip = '';
			}
		},
		// 将任意浏览器可解码的音频（wav/mp3 等）解码并重采样为 24000Hz 单声道 Int16 PCM，
		// 作为 silk 编码标准输入（silk 仅支持 8k/12k/16k/24k，故必须重采样）
		async decodeToSilkPcm(arrayBuffer) {
			const AudioCtx = window.AudioContext || window.webkitAudioContext;
			const audioCtx = new AudioCtx();
			try {
				const audioBuffer = await audioCtx.decodeAudioData(arrayBuffer);
				const offlineCtx = new OfflineAudioContext(
					audioBuffer.numberOfChannels,
					Math.ceil(audioBuffer.duration * SILK_SAMPLE_RATE),
					SILK_SAMPLE_RATE
				);
				const source = offlineCtx.createBufferSource();
				source.buffer = audioBuffer;
				source.connect(offlineCtx.destination);
				source.start();
				const rendered = await offlineCtx.startRendering();
				// 多声道混为单声道 Int16（小端，与 silk-wasm 输入约定一致）
				const channels = [];
				for (let ch = 0; ch < rendered.numberOfChannels; ch++) {
					channels.push(rendered.getChannelData(ch));
				}
				const pcm = new Int16Array(rendered.length);
				for (let i = 0; i < rendered.length; i++) {
					let v = 0;
					for (let ch = 0; ch < channels.length; ch++) {
						v += channels[ch][i];
					}
					v /= channels.length;
					pcm[i] = Math.max(-1, Math.min(1, v)) * 32767;
				}
				return pcm.buffer;
			} finally {
				audioCtx.close();
			}
		},
		// 文件选择：silk 直传，其他格式转码后上传
		async onFileChange(file) {
			const raw = file.raw;
			if (!raw) {
				return;
			}
			// 录音进行中先结束录音，避免上传期间录音按钮全禁用导致录音静默延长
			if (this.recordState === 'RUNNING' || this.recordState === 'PAUSE') {
				this.$message.warning('请先结束当前录音，再选择语音文件');
				return;
			}
			// 已有录音结果时禁选文件，避免与"保存语音"并发上传互相覆盖（后完成者胜出）
			if (this.recordState === 'COMPLETE') {
				this.$message.warning('当前已有录音，请先保存语音或重新录音，再选择语音文件');
				return;
			}
			if (raw.size === 0) {
				this.$message.error('语音文件大小为 0，请重新选择');
				return;
			}
			if (raw.size > MAX_VOICE_SIZE) {
				this.$message.error('语音文件不允许超过 5MB，请重新选择');
				return;
			}
			this.sending = true;
			this.sendingTip = '正在解析语音文件...';
			// 捕获代际令牌：期间发生清理/销毁则丢弃结果
			const seq = this.uploadSeq;
			try {
				const arrayBuffer = await raw.arrayBuffer();
				let silkData;
				let durationMs;
				if (isSilk(arrayBuffer)) {
					// 已是微信 silk 语音，直接校验时长
					silkData = arrayBuffer;
					durationMs = getDuration(arrayBuffer);
				} else {
					// 其他格式：解码重采样后编码为 silk
					this.sendingTip = '正在转码为微信语音格式...';
					const pcm = await this.decodeToSilkPcm(arrayBuffer);
					const res = await encode(pcm, SILK_SAMPLE_RATE);
					silkData = res.data;
					durationMs = res.duration;
				}
				const duration = Math.round(durationMs / 1000);
				// 微信语音上限 60 秒
				if (duration > 60) {
					this.$message.warning('语音时长不能超过 60 秒，请重新选择');
					return;
				}
				// 过短无法编码发送
				if (duration < 1) {
					this.$message.warning('语音太短，请重新选择');
					return;
				}
				this.sendingTip = '正在上传...';
				const silkBlob = new Blob([silkData], { type: 'application/octet-stream' });
				const formData = new window.FormData();
				formData.append('file', silkBlob, new Date().getTime() + '.silk');
				// 上传到后端（返回 { path, size, name }）
				const data = await uploadVoice(formData);
				// 期间控件被清理（关闭/切类型/重开）则丢弃结果，避免过期语音写入表单
				if (seq !== this.uploadSeq) {
					return;
				}
				this.savedDuration = Math.min(60, Math.max(1, duration));
				this.$emit('send', {
					// 取整后钳制到 60，避免越过后端上限校验
					duration: this.savedDuration,
					url: data.path,
					size: silkBlob.size
				});
			} catch (error) {
				this.$message.error('语音解析或上传失败：' + (error && error.message ? error.message : error));
			} finally {
				this.sending = false;
				this.sendingTip = '';
			}
		}
	}
}
</script>

<style lang="scss" scoped>
.voice-record-control {
	width: 100%;

	.voice-existing-tip,
	.voice-saved-tip {
		font-size: 13px;
		color: #67c23a;
		margin-bottom: 8px;
	}

	.record-area {
		padding: 12px;
		border: 1px dashed #dcdfe6;
		border-radius: 6px;

		.record-tip {
			font-size: 13px;
			color: #606266;
		}

		.record-duration {
			font-size: 13px;
			color: #909399;
			margin: 4px 0 8px;
		}

		.record-btn-group {
			.el-button + .el-button {
				margin-left: 8px;
			}
		}
	}

	.picker-divider {
		display: flex;
		align-items: center;
		margin: 14px 0 10px;
		color: #909399;
		font-size: 12px;

		&::before,
		&::after {
			content: '';
			flex: 1;
			height: 1px;
			background: #e4e7ed;
		}

		span {
			padding: 0 12px;
		}
	}

	.voice-uploader {
		width: 100%;

		::v-deep .el-upload {
			width: 100%;
		}

		::v-deep .el-upload-dragger {
			width: 100%;
			padding: 24px 0;
		}
	}

	.voice-processing-tip {
		margin-top: 8px;
		font-size: 13px;
		color: #909399;

		i {
			margin-right: 4px;
		}
	}
}
</style>
